import { useCallback, useEffect, useSyncExternalStore } from "react";
import type { Bbo, InstrumentInfo, MdStatus, TradeBatch } from "@helm/core";
import { renderHost, type WidgetPrints } from "../chart/renderHost";

/** The instrument facts the widgets use (subset of proto `InstrumentInfo`). */
export type InstrumentFacts = Pick<
  InstrumentInfo,
  "symbol" | "exchange" | "tickSizeNum" | "tickSizeDen" | "displayDecimals"
> & {
  /** One tick on one contract, integer cents (proto `tick_value_cents`, NQ 500); 0 = unknown. */
  tickValueCents: number;
};
/** Structural subsets of the proto messages, so a synthetic feed needs no protobuf runtime. */
export type BboLike = Pick<Bbo, "bidSize" | "askSize"> & {
  bidTicks: Bbo["bidTicks"] | number;
  askTicks: Bbo["askTicks"] | number;
};
export type TradesLike = Pick<TradeBatch, "priceTicks" | "size" | "aggressor" | "tsNs">;
import type { PriceFormat } from "@helm/ui";

/**
 * Main-thread market data for the DOM ladder, Time & Sales and Watchlist.
 *
 * One md socket per window (SPEC §12.0): the chart's render worker owns it, and the hub asks the
 * worker to `watch` the symbols its widgets hold (ref-counted here, one watch per symbol). The
 * worker decodes off the main thread and forwards only watched symbols: instrument info, prints
 * (transferred typed arrays) and BBO (latest-wins, ~60 Hz). See docs/chart-engine.md.
 *
 * Read-only: watch/unwatch and receive. Notifications are coalesced to one per animation frame per
 * symbol so a fast tape cannot flood React.
 */

export type Print = {
  priceTicks: number;
  size: number;
  /** 1 buy aggressor, 2 sell aggressor, 0 unknown (proto TradeBatch.aggressor). */
  aggressor: number;
  /** Exchange time, epoch ms. */
  tsMs: number;
  /** Monotonic id for React keys. */
  id: number;
};

export type SymbolMarket = {
  requested: string;
  info: InstrumentFacts | null;
  bidTicks: number | null;
  bidSize: number | null;
  askTicks: number | null;
  askSize: number | null;
  lastTicks: number | null;
  lastSize: number | null;
  /** Volume traded at each price since this window subscribed. */
  volumeAtPrice: ReadonlyMap<number, number>;
  /** First print price since this window subscribed (screener reference until SessionStats). */
  firstTicks: number | null;
  /** Contracts and prints since this window subscribed. */
  volume: number;
  tradeCount: number;
  /** Newest first, capped at MAX_PRINTS. */
  prints: readonly Print[];
  /** Why there is no data, when there is none. */
  notice: string | null;
  /** Browser time (epoch ms) of the last trade or quote received; null before the first (CB3). */
  lastUpdateMs: number | null;
  /**
   * When this window started watching the symbol (epoch ms): the stale badge counts from here
   * until the first update.
   */
  watchSinceMs: number;
};

export type HubStatus = MdStatus | "off";

const MAX_PRINTS = 300;

function empty(requested: string): SymbolMarket {
  return {
    requested,
    info: null,
    bidTicks: null,
    bidSize: null,
    askTicks: null,
    askSize: null,
    lastTicks: null,
    lastSize: null,
    volumeAtPrice: new Map(),
    firstTicks: null,
    volume: 0,
    tradeCount: 0,
    prints: [],
    notice: null,
    lastUpdateMs: null,
    watchSinceMs: Date.now(),
  };
}

export function priceFormatOf(info: InstrumentFacts | null): PriceFormat | null {
  if (!info || info.tickSizeDen === 0) return null;
  return {
    tickSizeNum: info.tickSizeNum,
    tickSizeDen: info.tickSizeDen,
    displayDecimals: info.displayDecimals,
  };
}

type Listener = () => void;

type Feed = { watch(symbol: string, on: boolean): void; setMarketFeed(sink: null): void };

class MarketDataHub {
  private feed: Feed | null = null;
  private live = false;
  private status: HubStatus = "off";
  private readonly refs = new Map<string, number>();
  private readonly state = new Map<string, SymbolMarket>();
  /** Resolved contract ("NQZ6") → requested root ("NQ"). */
  private readonly byResolved = new Map<string, string>();
  private readonly listeners = new Map<string, Set<Listener>>();
  private readonly statusListeners = new Set<Listener>();
  private readonly anyListeners = new Set<Listener>();
  private readonly dirty = new Set<string>();
  private formatsRev = 0;
  private frame: number | null = null;
  private printSeq = 0;

  /** Go live (signed in): attach to the window's shared md socket in the render worker. */
  enableLive(): void {
    if (this.live) return;
    this.live = true;
    this.setStatus("connecting");
    const host = renderHost();
    host.setMarketFeed({
      status: (s) => {
        this.setStatus(s);
      },
      instrument: (requested, info) => {
        this.setInstrument(requested, info);
      },
      trades: (requested, prints) => {
        this.ingestPrints(requested, prints);
      },
      quotes: (quotes) => {
        for (const q of quotes) this.ingestBbo(q.requested, q);
      },
      notice: (requested, text) => {
        this.patch(requested, { notice: text });
      },
    });
    this.feed = host;
    for (const sym of this.refs.keys()) host.watch(sym, true);
  }

  disableLive(): void {
    const feed = this.feed;
    this.feed = null;
    if (feed) {
      for (const sym of this.refs.keys()) feed.watch(sym, false);
      feed.setMarketFeed(null);
    }
    this.live = false;
    this.setStatus("off");
  }

  /** Used by the dev-only preview's synthetic feed. */
  setStatus(s: HubStatus): void {
    this.status = s;
    for (const l of this.statusListeners) l();
  }

  getStatus(): HubStatus {
    return this.status;
  }

  onStatus(l: Listener): () => void {
    this.statusListeners.add(l);
    return () => {
      this.statusListeners.delete(l);
    };
  }

  retain(symbol: string): void {
    const n = this.refs.get(symbol) ?? 0;
    this.refs.set(symbol, n + 1);
    if (!this.state.has(symbol)) this.state.set(symbol, empty(symbol));
    if (n === 0) this.feed?.watch(symbol, true);
  }

  release(symbol: string): void {
    const n = this.refs.get(symbol) ?? 0;
    if (n <= 1) {
      this.refs.delete(symbol);
      this.feed?.watch(symbol, false);
      this.state.delete(symbol);
      this.formatsRev++;
      // Another widget may still watch the same contract under another name ("NQ" and "NQZ6" both
      // resolve to NQZ6): re-point the contract at it instead of forgetting it.
      for (const [resolved, req] of this.byResolved) {
        if (req !== symbol) continue;
        const other = [...this.state].find(([, m]) => m.info?.symbol === resolved)?.[0];
        if (other === undefined) this.byResolved.delete(resolved);
        else this.byResolved.set(resolved, other);
      }
    } else {
      this.refs.set(symbol, n - 1);
    }
  }

  get(symbol: string): SymbolMarket | undefined {
    return this.state.get(symbol);
  }

  /**
   * Market for a resolved contract ("NQZ6"), as positions and orders name it: the widget that
   * requested it directly, else one whose root resolved to it ("NQ" → "NQZ6"). Prefers entries
   * that already have instrument info.
   */
  findByContract(contract: string): SymbolMarket | undefined {
    const direct = this.state.get(contract);
    if (direct?.info) return direct;
    const req = this.byResolved.get(contract);
    const viaRoot = req === undefined ? undefined : this.state.get(req);
    return viaRoot ?? direct;
  }

  /** Called (once per animation frame at most) after any symbol changed. */
  onAnyChange(l: Listener): () => void {
    this.anyListeners.add(l);
    return () => {
      this.anyListeners.delete(l);
    };
  }

  listen(symbol: string, l: Listener): () => void {
    let set = this.listeners.get(symbol);
    if (!set) {
      set = new Set();
      this.listeners.set(symbol, set);
    }
    set.add(l);
    return () => {
      set.delete(l);
    };
  }

  /**
   * Bumped whenever a contract's instrument facts arrive or a watched symbol is dropped: readers
   * that format prices of contracts they do not watch themselves (the Orders / Fills tables via
   * `findByContract`) key their memo on it, so a price format loading later reaches them.
   */
  get formatsVersion(): number {
    return this.formatsRev;
  }

  setInstrument(requested: string, info: InstrumentFacts): void {
    this.formatsRev++;
    this.byResolved.set(info.symbol, requested);
    this.patch(requested, { info, notice: null });
  }

  ingestBbo(requested: string, b: BboLike): void {
    if (!this.state.has(requested)) return;
    this.patch(requested, {
      bidTicks: Number(b.bidTicks),
      bidSize: b.bidSize,
      askTicks: Number(b.askTicks),
      askSize: b.askSize,
      lastUpdateMs: Date.now(),
    });
  }

  /** Proto-shaped prints (the dev preview's synthetic feed). */
  ingestTrades(requested: string, t: TradesLike): void {
    const n = t.priceTicks.length;
    const prints: WidgetPrints = {
      price: new Float64Array(n),
      size: new Float64Array(n),
      aggr: new Uint8Array(n),
      tsMs: new Float64Array(n),
    };
    for (let i = 0; i < n; i++) {
      prints.price[i] = Number(t.priceTicks[i] ?? 0n);
      prints.size[i] = t.size[i] ?? 0;
      prints.aggr[i] = t.aggressor[i] ?? 0;
      prints.tsMs[i] = Number((t.tsNs[i] ?? 0n) / 1_000_000n);
    }
    this.ingestPrints(requested, prints);
  }

  /** Decoded prints from the render worker, in arrival order. */
  ingestPrints(requested: string, t: WidgetPrints): void {
    const cur = this.state.get(requested);
    if (!cur || t.price.length === 0) return;
    const vap = new Map(cur.volumeAtPrice);
    const fresh: Print[] = [];
    let volume = cur.volume;
    for (let i = 0; i < t.price.length; i++) {
      const px = t.price[i] ?? 0;
      const size = t.size[i] ?? 0;
      vap.set(px, (vap.get(px) ?? 0) + size);
      volume += size;
      fresh.push({
        priceTicks: px,
        size,
        aggressor: t.aggr[i] ?? 0,
        tsMs: t.tsMs[i] ?? 0,
        id: ++this.printSeq,
      });
    }
    fresh.reverse();
    const last = fresh[0];
    this.patch(requested, {
      volumeAtPrice: vap,
      firstTicks: cur.firstTicks ?? t.price[0] ?? null,
      volume,
      tradeCount: cur.tradeCount + t.price.length,
      prints: [...fresh, ...cur.prints].slice(0, MAX_PRINTS),
      lastTicks: last?.priceTicks ?? cur.lastTicks,
      lastSize: last?.size ?? cur.lastSize,
      lastUpdateMs: Date.now(),
    });
  }

  private patch(requested: string, p: Partial<SymbolMarket>): void {
    const cur = this.state.get(requested);
    if (!cur) return;
    this.state.set(requested, { ...cur, ...p });
    this.dirty.add(requested);
    this.schedule();
  }

  private schedule(): void {
    if (this.frame !== null) return;
    const flush = (): void => {
      this.frame = null;
      const syms = [...this.dirty];
      this.dirty.clear();
      for (const s of syms) for (const l of this.listeners.get(s) ?? []) l();
      if (syms.length > 0) for (const l of this.anyListeners) l();
    };
    this.frame =
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame(flush)
        : (setTimeout(flush, 16) as unknown as number);
  }
}

export const marketData = new MarketDataHub();

/** Subscribe a widget to a symbol for as long as it is mounted; returns the live snapshot. */
export function useMarket(symbol: string | null): SymbolMarket | null {
  useEffect(() => {
    if (symbol === null) return;
    marketData.retain(symbol);
    return () => {
      marketData.release(symbol);
    };
  }, [symbol]);
  const subscribe = useCallback(
    (l: () => void) => (symbol === null ? () => {} : marketData.listen(symbol, l)),
    [symbol],
  );
  return useSyncExternalStore(subscribe, () => (symbol === null ? null : (marketData.get(symbol) ?? null)));
}

const subscribeStatus = (l: () => void): (() => void) => marketData.onStatus(l);
const getStatus = (): HubStatus => marketData.getStatus();

export function useMdStatus(): HubStatus {
  return useSyncExternalStore(subscribeStatus, getStatus);
}

/** One-line empty-state reason for market-data widgets. */
export function mdEmptyReason(status: HubStatus, m: SymbolMarket | null): string {
  if (m?.notice) return m.notice;
  switch (status) {
    case "off":
      return "Market data is offline — sign in to stream quotes.";
    case "connecting":
      return "Connecting to market data…";
    case "disconnected":
      return "Market data disconnected — reconnecting…";
    case "connected":
      return m?.info ? "Waiting for the first quote…" : "Resolving the contract…";
  }
}
